import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './ModulesEditor.module.css';
import { Module } from '../../types/course.types';
import { Lesson } from '../hooks/useModuleLessons';

import { tr } from '@/i18n';
interface ModulesEditorProps {
  courseId: string;
  modules: Module[];
  getLessons: (moduleId: string) => Lesson[];
  isModuleLoading: (moduleId: string) => boolean;
  onAddModule: () => void;
  onDeleteModule: (id: string) => void;
  onDuplicateModule: (id: string) => void;
  onUpdateModuleTitle: (id: string, title: string) => void;
  onAddLesson: (moduleId: string) => Promise<Lesson | null>;
  onUpdateLessonTitle: (moduleId: string, lessonId: string, title: string) => void;
  onDeleteLesson: (moduleId: string, lessonId: string) => void;
  onBack: () => void;
}

// ── Inline SVG Icons ──────────────────────────────────────────
const DragIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="5" r="1" /><circle cx="15" cy="5" r="1" />
    <circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" />
    <circle cx="9" cy="19" r="1" /><circle cx="15" cy="19" r="1" />
  </svg>
);

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="5" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// ── Dropdown Components ───────────────────────────────────────
const ModuleDropdown: React.FC<{
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ onDuplicate, onDelete, onClose }) => (
  <div className={styles.dropdown}>
    <button className={styles.dropdownItem} onClick={() => { onDuplicate(); onClose(); }}>
      <CopyIcon /> {tr("ui.duplicate", "Duplicate")}
    </button>
    <button className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`} onClick={() => { onDelete(); onClose(); }}>
      <TrashIcon /> {tr('common.delete', 'Delete')}
    </button>
  </div>
);

const LessonDropdown: React.FC<{
  onDelete: () => void;
  onClose: () => void;
}> = ({ onDelete, onClose }) => (
  <div className={styles.dropdown}>
    <button className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`} onClick={() => { onDelete(); onClose(); }}>
      <TrashIcon /> {tr('common.delete', 'Delete')}
    </button>
  </div>
);

// ══════════════════════════════════════════════════════════════
//  ModulesEditor
// ══════════════════════════════════════════════════════════════
export const ModulesEditor: React.FC<ModulesEditorProps> = ({
  courseId: _courseId,
  modules,
  getLessons,
  isModuleLoading,
  onAddModule,
  onDeleteModule,
  onDuplicateModule,
  onUpdateModuleTitle,
  onAddLesson,
  onUpdateLessonTitle,
  onDeleteLesson,
  onBack,
}) => {
  const [openModuleMenuId, setOpenModuleMenuId] = useState<string | null>(null);
  const [openLessonMenuId, setOpenLessonMenuId] = useState<string | null>(null);
  const [addingLesson, setAddingLesson] = useState<string | null>(null);

  const handleAddLesson = async (moduleId: string) => {
    setAddingLesson(moduleId);
    await onAddLesson(moduleId);
    setAddingLesson(null);
  };

  return (
    <div className={styles.container}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeftIcon /> {tr("ui.back_to_course_639acf", "Back to Course")}
      </button>

      {modules.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📚</div>
          <p className={styles.emptyText}>
            {tr("ui.no_modules_yet", "No modules yet.")}{'\n'}{tr("ui.create_your_first_module_to_start_building_your_4f2328", "Create your first module to start building your course.")}
          </p>
          <button className={styles.addModuleBtn} onClick={onAddModule}>
            <span className={styles.addModuleBtnIcon}>+</span>
            {tr("ui.add_first_module", "Add first module")}
          </button>
        </div>
      ) : (
        <>
          <div className={styles.modulesList}>
            {modules.map((module, mi) => {
              const lessons = getLessons(module.id);
              const loading = isModuleLoading(module.id);

              return (
                <div key={module.id} className={styles.moduleCard}>
                  <div className={styles.moduleTop}>
                    <div className={styles.moduleDrag}>
                      <DragIcon />
                    </div>
                    <div className={styles.moduleBody}>
                      <div className={styles.moduleRow}>
                        <span className={styles.moduleLabel}>
                          <span className={styles.moduleIcon}>📁</span>
                          {tr("ui.module_1cf5e1", "MODULE")} {mi + 1}
                        </span>
                        <div className={styles.menuWrapper}>
                          <button
                            className={`${styles.menuBtn} ${openModuleMenuId === module.id ? styles.menuBtnActive : ''}`}
                            onClick={() => setOpenModuleMenuId(p => p === module.id ? null : module.id)}
                          >
                            <DotsIcon />
                          </button>
                          {openModuleMenuId === module.id && (
                            <ModuleDropdown
                              onDuplicate={() => onDuplicateModule(module.id)}
                              onDelete={() => onDeleteModule(module.id)}
                              onClose={() => setOpenModuleMenuId(null)}
                            />
                          )}
                        </div>
                      </div>

                      <input
                        className={styles.moduleTitleInput}
                        defaultValue={module.title}
                        placeholder={tr("ui.add_a_module_title", "Add a module title...")}
                        onBlur={e => {
                          const val = e.target.value.trim();
                          if (val && val !== module.title) onUpdateModuleTitle(module.id, val);
                        }}
                      />
                      <input
                        className={styles.moduleDescInput}
                        placeholder={tr("ui.add_a_short_description", "Add a short description...")}
                      />

                      {/* Lessons */}
                      <div className={styles.lessonsList}>
                        {loading ? (
                          <div className={styles.lessonsLoading}>
                            <div className={styles.lessonsSpinner} />
                          </div>
                        ) : (
                          lessons.map((lesson, li) => (
                            <div key={lesson.id} className={styles.lessonCard}>
                              <div className={styles.lessonDrag}>
                                <DragIcon />
                              </div>
                              <div className={styles.lessonBody}>
                                <span className={styles.lessonLabel}>
                                  {tr("ui.lesson", "📖 LESSON")} {li + 1}
                                </span>
                                <input
                                  className={styles.lessonTitleInput}
                                  defaultValue={lesson.title}
                                  placeholder={tr("ui.add_a_lesson_title", "Add a lesson title...")}
                                  onBlur={e => {
                                    const val = e.target.value;
                                    if (val !== lesson.title) onUpdateLessonTitle(module.id, lesson.id, val);
                                  }}
                                />
                              </div>
                              <div className={styles.lessonActions}>
                                <Link
                                  to={`/admin/dashboard/learning/courses/module/${lesson.id}/edit`}
                                  className={styles.editContentBtn}
                                >
                                  {tr("ui.edit_content_61f03c", "Edit content")}
                                </Link>
                                <button
                                  className={styles.aiGenerateBtn}
                                  title={tr("ui.generate_with_ai", "Generate with AI")}
                                >
                                  <SparkleIcon />
                                </button>
                                <div className={styles.lessonMenuWrapper}>
                                  <button
                                    className={styles.lessonMenuBtn}
                                    onClick={() => setOpenLessonMenuId(p => p === lesson.id ? null : lesson.id)}
                                  >
                                    <DotsIcon />
                                  </button>
                                  {openLessonMenuId === lesson.id && (
                                    <LessonDropdown
                                      onDelete={() => onDeleteLesson(module.id, lesson.id)}
                                      onClose={() => setOpenLessonMenuId(null)}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <button
                        className={styles.addLessonBtn}
                        onClick={() => handleAddLesson(module.id)}
                        disabled={addingLesson === module.id}
                      >
                        {addingLesson === module.id ? (
                          <><div className={styles.lessonsSpinner} style={{ width: 14, height: 14 }} /> {tr("ui.adding", "Adding...")}</>
                        ) : (
                          tr("ui.add_new_lesson", "+ Add new lesson")
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button className={styles.addModuleBtn} onClick={onAddModule}>
            <span className={styles.addModuleBtnIcon}>+</span>
            {tr("ui.add_new_module", "Add new module")}
          </button>
        </>
      )}
    </div>
  );
};